import imageCompression from "browser-image-compression";
import { ensureValidImageFile } from "lib/ensureValidImageFile";

const DEFAULT_ADJUSTMENTS = {
  brightness: 100,
  contrast: 100,
  saturation: 100,
  sharpness: 0,
};

export { DEFAULT_ADJUSTMENTS };

export function getRadianAngle(degreeValue) {
  return (degreeValue * Math.PI) / 180;
}

function rotateSize(width, height, rotation) {
  const rotRad = getRadianAngle(rotation);
  return {
    width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

/**
 * Load image for canvas. crossOrigin is only set for http(s) - never on blob:/data: URLs.
 */
export function createImage(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", () => reject(new Error("Failed to load image")));
    if (typeof url === "string" && /^https?:/i.test(url)) {
      image.crossOrigin = "anonymous";
    }
    image.src = url;
  });
}

function parseMerchantCdnPath(url) {
  if (!url || typeof url !== "string") return null;
  const match = url.match(/\/([^/]+)\/([^/]+)\/(original|small|large|blurred)(\.\w+)$/i);
  if (!match) return null;
  return {
    merchantId: match[1],
    imageId: match[2],
    type: match[3],
    extension: match[4],
  };
}

async function fetchUrlAsBlob(url) {
  const res = await fetch(url, { mode: "cors", credentials: "omit", cache: "no-store" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.blob();
}

/** Try CDN direct, then webshop same-origin proxy, then authenticated dashboard fetch. */
async function fetchMerchantImageBlob(cdnUrl) {
  const errors = [];

  try {
    return await fetchUrlAsBlob(cdnUrl);
  } catch (e) {
    errors.push(e);
  }

  const parsed = parseMerchantCdnPath(cdnUrl);
  if (parsed) {
    const ext = parsed.extension.replace(/^\./, "");
    const proxyBase =
      process.env.REACT_APP_WEBSHOP_ENGINE_URL || "http://localhost:3000";
    const proxyUrl = `${proxyBase.replace(/\/$/, "")}/api/img/${encodeURIComponent(parsed.merchantId)}/${encodeURIComponent(parsed.imageId)}/original.${ext}`;
    try {
      return await fetchUrlAsBlob(proxyUrl);
    } catch (e) {
      errors.push(e);
    }
  }

  throw errors[0] || new Error("Could not download image");
}

function fileNameFromSource(fileOrUrl, fallback = "image.jpg") {
  if (fileOrUrl instanceof File && fileOrUrl.name) return fileOrUrl.name;
  if (typeof fileOrUrl === "string") {
    try {
      const path = new URL(fileOrUrl, window.location.origin).pathname;
      const base = path.split("/").pop();
      if (base) return base;
    } catch {
      /* ignore */
    }
  }
  return fallback;
}

/** Load remote CDN/API images as blob URL for canvas-safe editing. */
export async function loadImageSource(fileOrUrl) {
  if (!fileOrUrl) return null;

  if (fileOrUrl instanceof File || fileOrUrl instanceof Blob) {
    const blob =
      fileOrUrl instanceof File
        ? fileOrUrl
        : new File([fileOrUrl], "image.jpg", { type: fileOrUrl.type || "image/jpeg" });
    return {
      blob,
      url: URL.createObjectURL(blob),
      fileName: fileNameFromSource(fileOrUrl),
    };
  }

  if (typeof fileOrUrl === "string") {
    try {
      const blob = await fetchMerchantImageBlob(fileOrUrl);
      const type = blob.type && blob.type.startsWith("image/") ? blob.type : "image/jpeg";
      const normalized = blob.type ? blob : new Blob([blob], { type });
      return {
        blob: normalized,
        url: URL.createObjectURL(normalized),
        fileName: fileNameFromSource(fileOrUrl),
      };
    } catch {
      return {
        blob: null,
        url: fileOrUrl,
        fileName: fileNameFromSource(fileOrUrl),
        corsRisk: true,
      };
    }
  }

  return null;
}

function dataUrlToBlob(dataUrl) {
  const [header, data] = dataUrl.split(",");
  const mime = header.match(/:(.*?);/)?.[1] || "image/jpeg";
  const binary = atob(data);
  const len = binary.length;
  const arr = new Uint8Array(len);
  for (let i = 0; i < len; i++) arr[i] = binary.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

function canvasToBlob(canvas, { quality = 0.92, preferPng = false } = {}) {
  const primaryType = preferPng ? "image/png" : "image/jpeg";
  const fallbackType = preferPng ? "image/jpeg" : "image/png";

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
          return;
        }
        canvas.toBlob(
          (fallbackBlob) => {
            if (fallbackBlob) {
              resolve(fallbackBlob);
              return;
            }
            try {
              resolve(
                dataUrlToBlob(
                  canvas.toDataURL(primaryType, preferPng ? undefined : quality)
                )
              );
            } catch (err) {
              reject(
                new Error(
                  "Could not export image. Upload the file directly instead of using a library image, or check CDN/CORS settings."
                )
              );
            }
          },
          fallbackType,
          preferPng ? undefined : quality
        );
      },
      primaryType,
      preferPng ? undefined : quality
    );
  });
}

function clampByte(v) {
  return Math.min(255, Math.max(0, Math.round(v)));
}

function applyColorAdjustments(imageData, adjustments) {
  const data = imageData.data;
  const bMul = (adjustments?.brightness ?? 100) / 100;
  const cMul = (adjustments?.contrast ?? 100) / 100;
  const sMul = (adjustments?.saturation ?? 100) / 100;

  for (let i = 0; i < data.length; i += 4) {
    let r = data[i];
    let g = data[i + 1];
    let b = data[i + 2];

    r *= bMul;
    g *= bMul;
    b *= bMul;

    r = (r - 128) * cMul + 128;
    g = (g - 128) * cMul + 128;
    b = (b - 128) * cMul + 128;

    const gray = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    r = gray + (r - gray) * sMul;
    g = gray + (g - gray) * sMul;
    b = gray + (b - gray) * sMul;

    data[i] = clampByte(r);
    data[i + 1] = clampByte(g);
    data[i + 2] = clampByte(b);
  }
  return imageData;
}

function applyUnsharpMaskToCanvas(canvas, amount) {
  const strength = (amount ?? 0) / 100;
  if (strength <= 0) return;

  const w = canvas.width;
  const h = canvas.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const original = ctx.getImageData(0, 0, w, h);
  const blurCanvas = document.createElement("canvas");
  blurCanvas.width = w;
  blurCanvas.height = h;
  const blurCtx = blurCanvas.getContext("2d");
  if (!blurCtx) return;

  const blurPx = Math.max(0.6, Math.min(3, 0.5 + strength * 2.5));
  blurCtx.filter = `blur(${blurPx}px)`;
  blurCtx.drawImage(canvas, 0, 0);
  blurCtx.filter = "none";
  const blurred = blurCtx.getImageData(0, 0, w, h);

  const out = ctx.createImageData(w, h);
  const amountScale = strength * 2.2;
  for (let i = 0; i < original.data.length; i += 4) {
    for (let c = 0; c < 3; c++) {
      const o = original.data[i + c];
      const bl = blurred.data[i + c];
      out.data[i + c] = clampByte(o + amountScale * (o - bl));
    }
    out.data[i + 3] = original.data[i + 3];
  }
  ctx.putImageData(out, 0, 0);
}

function applyAdjustmentsToCanvas(canvas, adjustments) {
  if (!canvas || !adjustments) return;
  const w = canvas.width;
  const h = canvas.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const needsColor =
    adjustments.brightness !== 100 ||
    adjustments.contrast !== 100 ||
    adjustments.saturation !== 100;
  const needsSharp = (adjustments.sharpness ?? 0) > 0;

  if (!needsColor && !needsSharp) return;

  if (needsColor) {
    const imgData = ctx.getImageData(0, 0, w, h);
    applyColorAdjustments(imgData, adjustments);
    ctx.putImageData(imgData, 0, 0);
  }
  if (needsSharp) {
    applyUnsharpMaskToCanvas(canvas, adjustments.sharpness);
  }
}

export function adjustmentsAreDefault(adjustments) {
  if (!adjustments) return true;
  return (
    adjustments.brightness === 100 &&
    adjustments.contrast === 100 &&
    adjustments.saturation === 100 &&
    (adjustments.sharpness ?? 0) === 0
  );
}

export async function analyzeImageForEnhance(imageSrc) {
  const image = await createImage(imageSrc);
  const width = image.naturalWidth || image.width;
  const height = image.naturalHeight || image.height;
  const maxDimension = Math.max(width, height);
  return {
    width,
    height,
    maxDimension,
    isLowRes: maxDimension < 1200,
    isVeryLowRes: maxDimension < 800,
  };
}

/**
 * Render full-size adjusted preview (same dimensions as source so crop coords stay valid).
 */
export async function renderAdjustedImagePreview(imageSrc, adjustments) {
  if (adjustmentsAreDefault(adjustments)) return null;

  const image = await createImage(imageSrc);
  const w = image.naturalWidth || image.width;
  const h = image.naturalHeight || image.height;

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas not supported");

  ctx.drawImage(image, 0, 0, w, h);
  applyAdjustmentsToCanvas(canvas, adjustments);
  return canvasToBlob(canvas, { preferPng: false, quality: 0.92 });
}

/**
 * Crop + adjust using the react-easy-crop export pattern (rotation-safe).
 */
export async function getCroppedImage({
  imageSrc,
  pixelCrop,
  rotation = 0,
  flip = { horizontal: false, vertical: false },
  adjustments = DEFAULT_ADJUSTMENTS,
  preferPng = false,
}) {
  if (!pixelCrop?.width || !pixelCrop?.height) {
    throw new Error("Crop area not ready - move or zoom the image slightly, then try again.");
  }

  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas not supported");

  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
    image.naturalWidth || image.width,
    image.naturalHeight || image.height,
    rotation
  );

  canvas.width = Math.max(1, Math.floor(bBoxWidth));
  canvas.height = Math.max(1, Math.floor(bBoxHeight));

  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(getRadianAngle(rotation));
  ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
  ctx.translate(-(image.naturalWidth || image.width) / 2, -(image.naturalHeight || image.height) / 2);
  ctx.drawImage(image, 0, 0);

  const cropW = Math.max(1, Math.floor(pixelCrop.width));
  const cropH = Math.max(1, Math.floor(pixelCrop.height));
  const cropX = Math.max(0, Math.floor(pixelCrop.x));
  const cropY = Math.max(0, Math.floor(pixelCrop.y));

  const croppedCanvas = document.createElement("canvas");
  croppedCanvas.width = cropW;
  croppedCanvas.height = cropH;
  const croppedCtx = croppedCanvas.getContext("2d");
  if (!croppedCtx) throw new Error("Canvas not supported");

  croppedCtx.drawImage(canvas, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);
  applyAdjustmentsToCanvas(croppedCanvas, adjustments);

  return canvasToBlob(croppedCanvas, { preferPng });
}

/**
 * Composite a transparent PNG onto a solid background and export JPEG for merchant CDN upload.
 * The image API reliably processes JPEG; large RGBA PNGs often fail server-side.
 */
export async function flattenTransparentToJpeg(
  blob,
  fileName = "image.jpg",
  { maxDimension = 3840, background = "#ffffff" } = {}
) {
  const url = URL.createObjectURL(blob);
  try {
    const image = await createImage(url);
    let width = image.naturalWidth || image.width;
    let height = image.naturalHeight || image.height;
    const maxSide = Math.max(width, height);
    if (maxSide > maxDimension) {
      const scale = maxDimension / maxSide;
      width = Math.max(1, Math.floor(width * scale));
      height = Math.max(1, Math.floor(height * scale));
    }

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas not supported");

    ctx.fillStyle = background;
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(image, 0, 0, width, height);

    const jpegBlob = await canvasToBlob(canvas, { preferPng: false, quality: 0.9 });
    const baseName = String(fileName || "image").replace(/\.[^.]+$/, "") || "image";
    return compressForWeb(jpegBlob, `${baseName}.jpg`, { preferPng: false });
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function computeAutoEnhanceAdjustments(quality = null) {
  if (quality?.isVeryLowRes) {
    return { brightness: 110, contrast: 118, saturation: 102, sharpness: 72 };
  }
  if (quality?.isLowRes) {
    return { brightness: 109, contrast: 115, saturation: 104, sharpness: 55 };
  }
  return { brightness: 108, contrast: 112, saturation: 105, sharpness: 32 };
}

export function computeProductEnhanceAdjustments(quality = null) {
  if (quality?.isVeryLowRes) {
    return { brightness: 114, contrast: 116, saturation: 98, sharpness: 75 };
  }
  if (quality?.isLowRes) {
    return { brightness: 112, contrast: 112, saturation: 98, sharpness: 58 };
  }
  return { brightness: 112, contrast: 108, saturation: 98, sharpness: 35 };
}

export function computeAutoEnhanceAdjustmentsForQuality(quality, { isProduct = false } = {}) {
  return isProduct
    ? computeProductEnhanceAdjustments(quality)
    : computeAutoEnhanceAdjustments(quality);
}

export async function compressForWeb(
  blob,
  fileName = "image.jpg",
  { preferPng = false, maxWidthOrHeight = 3840, initialQuality = 0.92, maxSizeMB = 3.5 } = {}
) {
  const baseName = String(fileName || "image").replace(/\.[^.]+$/, "") || "image";

  if (preferPng || blob.type === "image/png") {
    const pngName = `${baseName}.png`;
    const file =
      blob instanceof File
        ? ensureValidImageFile(blob, baseName)
        : new File([blob], pngName, { type: "image/png" });
    return ensureValidImageFile(file, baseName);
  }

  const inputName = `${baseName}.jpg`;
  const file =
    blob instanceof File
      ? ensureValidImageFile(blob, baseName)
      : new File([blob], inputName, { type: blob.type || "image/jpeg" });

  try {
    const compressed = await imageCompression(file, {
      maxSizeMB,
      maxWidthOrHeight,
      useWebWorker: true,
      fileType: "image/jpeg",
      initialQuality,
    });
    return ensureValidImageFile(compressed, baseName);
  } catch {
    return ensureValidImageFile(file, baseName);
  }
}

export async function exportEditedImage(options) {
  const preferPng = !!options.preferPng;
  const baseName = String(options.fileName || "image").replace(/\.[^.]+$/, "") || "image";

  let pixelCrop = options.pixelCrop;
  // Full-image export (no crop framing) — use natural dimensions when skipCrop is set.
  if (options.skipCrop && options.imageSrc) {
    const image = await createImage(options.imageSrc);
    pixelCrop = {
      x: 0,
      y: 0,
      width: image.naturalWidth || image.width,
      height: image.naturalHeight || image.height,
    };
  }

  const blob = await getCroppedImage({ ...options, pixelCrop, preferPng });

  let file;
  if (preferPng) {
    file = await flattenTransparentToJpeg(blob, `${baseName}.jpg`);
  } else {
    const exportPng = blob.type === "image/png";
    const ext = exportPng ? ".png" : ".jpg";
    file = await compressForWeb(blob, `${baseName}${ext}`, { preferPng: exportPng });
  }

  if (options.studioMeta) {
    file.studioMeta = options.studioMeta;
  }
  return file;
}

/** Build a File from an in-memory blob for "Original (full image)" pass-through. */
export function blobToImageFile(blob, fileName = "image.jpg") {
  if (blob instanceof File) return ensureValidImageFile(blob, fileName.replace(/\.[^.]+$/, "") || "image");
  const name = fileName || "image.jpg";
  const type = blob?.type && blob.type.startsWith("image/") ? blob.type : "image/jpeg";
  return ensureValidImageFile(new File([blob], name, { type }), name.replace(/\.[^.]+$/, "") || "image");
}
